import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import useReaderStore from "../../context/readerStore";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { searchEpub, searchPdf } from "../../services/searchService";
import "./SearchSidebar.css";

const escapeRegExp = (text) =>
  (text || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getOccurrenceCount = (result) => {
  const count = Number(result?.occurrenceCount || 1);
  return Number.isFinite(count) && count > 0 ? count : 1;
};

const getTotalOccurrences = (results) => {
  if (!Array.isArray(results) || results.length === 0) return 0;
  return results.reduce((sum, result) => sum + getOccurrenceCount(result), 0);
};

const SearchSidebar = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    setCurrentPage,
    document,
    documentType,
    totalPages,
    metadata,
    searchQuery: globalSearchQuery,
    setSearchQuery: setGlobalSearchQuery,
    clearSearchQuery,
    toggleSearchSidebar,
    hideSearchSidebar,
    searchResults: globalSearchResults,
    isSearching: globalIsSearching,
    setSearchResults: setGlobalSearchResults,
    setIsSearching: setGlobalIsSearching,
    isChunked,
    loadedChunks,
    loadChunk,
    loadEpubChapter,
    currentPage,
    epubCurrentChapterNumber,
  } = useReaderStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const searchIdRef = useRef(0);
  const seenResultsRef = useRef(new Set());
  const seenOccurrencesRef = useRef(new Set());
  const resultIndexByKeyRef = useRef(new Map());
  const resultsRef = useRef([]);
  const searchResultsContentRef = useRef(null);
  const pendingScrollRestoreRef = useRef(false);
  const savedScrollTopRef = useRef(0);
  const loadedChunksRef = useRef(loadedChunks);
  const currentPageRef = useRef(currentPage);
  const epubCurrentChapterNumberRef = useRef(epubCurrentChapterNumber);

  useEffect(() => {
    loadedChunksRef.current = loadedChunks;
  }, [loadedChunks]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    epubCurrentChapterNumberRef.current = epubCurrentChapterNumber;
  }, [epubCurrentChapterNumber]);

  // Limit how many background chunk searches run at once so we don't
  // overwhelm the main thread/network for large documents.
  const runTasksWithConcurrency = useCallback(async (tasks, limit) => {
    if (!tasks.length || limit <= 0) return;

    let index = 0;
    const worker = async () => {
      while (true) {
        const taskIndex = index;
        if (taskIndex >= tasks.length) return;
        index += 1;
        try {
          await tasks[taskIndex]();
        } catch (err) {
          // Individual task errors are already logged inside the task.
          // We don't fail the whole search because of a single chunk.
        }
        // Yield back to the event loop between tasks so the UI can update.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    };

    const workers = [];
    const poolSize = Math.min(limit, tasks.length);
    for (let i = 0; i < poolSize; i++) {
      workers.push(worker());
    }
    await Promise.allSettled(workers);
  }, []);

  // Debounce search input and progressively search unloaded chunks
  useEffect(() => {
    // When query is cleared, also clear global search state so highlights disappear
    if (!searchQuery.trim()) {
      if (globalSearchQuery && globalSearchQuery.trim()) {
        return;
      }

      searchIdRef.current += 1;
      seenResultsRef.current.clear();
      seenOccurrencesRef.current.clear();
      resultIndexByKeyRef.current.clear();
      resultsRef.current = [];

      setSearchResults([]);
      setIsSearching(false);
      setGlobalSearchResults([]);
      setGlobalIsSearching(false);
      setGlobalSearchQuery("");
      clearSearchQuery();
      return;
    }

    const currentSearchId = ++searchIdRef.current;
    seenResultsRef.current = new Set();
    seenOccurrencesRef.current = new Set();
    resultIndexByKeyRef.current = new Map();
    resultsRef.current = [];

    // Clear stale results immediately so the UI reflects that a new search
    // is in flight instead of keeping the previous query's matches visible.
    setSearchResults([]);
    setGlobalSearchResults([]);

    setIsSearching(true);
    setSearchError(null);
    setGlobalIsSearching(true);

    const appendResults = (newResults) => {
      if (searchIdRef.current !== currentSearchId) return;
      if (!Array.isArray(newResults) || newResults.length === 0) return;

      const merged = [...resultsRef.current];
      for (const r of newResults) {
        // Prefer backend/service-provided ids; fallback to legacy keys.
        const key =
          r.resultId ||
          r.cfi ||
          `${r.pageNumber}-${r.matchIndex}-${r.sectionIndex || ""}`;
        const occurrenceKey =
          r.occurrenceId ||
          `${r.pageNumber}-${r.matchIndex}-${r.sectionIndex || ""}-${(
            r.query || ""
          ).toLowerCase()}`;

        if (seenResultsRef.current.has(key)) {
          if (seenOccurrencesRef.current.has(occurrenceKey)) continue;

          seenOccurrencesRef.current.add(occurrenceKey);
          const existingIndex = resultIndexByKeyRef.current.get(key);
          if (typeof existingIndex === "number" && merged[existingIndex]) {
            const existing = merged[existingIndex];
            merged[existingIndex] = {
              ...existing,
              occurrenceCount:
                getOccurrenceCount(existing) + getOccurrenceCount(r),
            };
          }
          continue;
        }

        seenResultsRef.current.add(key);
        seenOccurrencesRef.current.add(occurrenceKey);

        const nextResult = {
          ...r,
          occurrenceCount: getOccurrenceCount(r),
        };
        resultIndexByKeyRef.current.set(key, merged.length);
        merged.push(nextResult);
      }

      resultsRef.current = merged;
      setSearchResults(merged);
      setGlobalSearchResults(merged);
    };

    const finalizeSearch = () => {
      if (searchIdRef.current !== currentSearchId) return;
      setIsSearching(false);
      setGlobalIsSearching(false);
    };

    const performSearch = async () => {
      const backgroundTasks = [];

      try {
        if (documentType === "pdf" && document) {
          const chunkSize = metadata?.chunkSize || 5;

          // Search already-loaded chunks first
          const initialResults = await searchPdf(
            document,
            searchQuery,
            totalPages,
            {
              isChunked: !!isChunked,
              loadedChunks: loadedChunksRef.current || {},
              chunkSize,
            },
          );
          appendResults(initialResults);

          // If chunked, search missing chunks in the background
          if (isChunked && totalPages && chunkSize) {
            const totalChunks = Math.ceil(totalPages / chunkSize);
            const loadedIndices = new Set(
              Object.keys(loadedChunksRef.current || {}).map((k) => Number(k)),
            );

            // Prioritize the chunk that contains the current page, then
            // expand outwards so the user sees nearby matches first.
            const currentChunkIndex =
              typeof currentPageRef.current === "number" &&
              currentPageRef.current > 0
                ? Math.floor((currentPageRef.current - 1) / chunkSize)
                : 0;

            const missingChunkIndices = [];
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
              if (loadedIndices.has(chunkIndex)) continue;
              missingChunkIndices.push(chunkIndex);
            }

            missingChunkIndices.sort((a, b) => {
              const da = Math.abs(a - currentChunkIndex);
              const db = Math.abs(b - currentChunkIndex);
              return da - db;
            });

            missingChunkIndices.forEach((chunkIndex) => {
              backgroundTasks.push(async () => {
                // If a new search has started, stop doing work for this one.
                if (searchIdRef.current !== currentSearchId) return;
                try {
                  const chunkDoc = await loadChunk(chunkIndex);
                  if (!chunkDoc) return;
                  if (searchIdRef.current !== currentSearchId) return;

                  const chunkResults = await searchPdf(
                    chunkDoc,
                    searchQuery,
                    chunkDoc.numPages,
                    {
                      isChunked: true,
                      loadedChunks: { [chunkIndex]: chunkDoc },
                      chunkSize,
                    },
                  );
                  appendResults(chunkResults);
                } catch (err) {
                  console.error(`Search chunk ${chunkIndex} error:`, err);
                }
              });
            });
          }
        } else if (documentType === "epub" && document) {
          // Search already-loaded chapters first
          const initialResults = await searchEpub(document, null, searchQuery, {
            isChunked: !!isChunked,
            loadedChunks: loadedChunksRef.current || {},
            metadata,
          });
          appendResults(initialResults);

          // If chunked, load and search missing chapters in the background
          if (isChunked && metadata?.chapters) {
            const loadedChapters = new Set(
              Object.keys(loadedChunksRef.current || {}).map((k) => Number(k)),
            );

            // Prioritize the current chapter, then nearby chapters so the
            // user sees local matches first.
            const currentChapter =
              typeof epubCurrentChapterNumberRef.current === "number" &&
              epubCurrentChapterNumberRef.current > 0
                ? epubCurrentChapterNumberRef.current
                : metadata.initialChapterNumber || 1;

            const missingChapters = metadata.chapters
              .map((chapter) => Number(chapter.chapter_number))
              .filter(
                (chapterNumber) =>
                  chapterNumber &&
                  !Number.isNaN(chapterNumber) &&
                  !loadedChapters.has(chapterNumber),
              )
              .sort((a, b) => {
                const da = Math.abs(a - currentChapter);
                const db = Math.abs(b - currentChapter);
                return da - db;
              });

            missingChapters.forEach((chapterNumber) => {
              backgroundTasks.push(async () => {
                if (searchIdRef.current !== currentSearchId) return;
                try {
                  const chapterDoc = await loadEpubChapter(chapterNumber);
                  if (!chapterDoc) return;
                  if (searchIdRef.current !== currentSearchId) return;

                  const chapterResults = await searchEpub(
                    chapterDoc,
                    null,
                    searchQuery,
                    {
                      isChunked: true,
                      loadedChunks: { [chapterNumber]: chapterDoc },
                      metadata,
                    },
                  );
                  appendResults(chapterResults);
                } catch (err) {
                  console.error(`Search chapter ${chapterNumber} error:`, err);
                }
              });
            });
          }
        }
      } catch (err) {
        console.error("Search error:", err);
        if (searchIdRef.current === currentSearchId) {
          setSearchError(err.message || t("errors.searchFailed"));
        }
      } finally {
        // Mark the main search as finished as soon as we've searched the
        // currently loaded chunks/chapters so the UI can stop showing a
        // blocking loader. Background work continues separately so results
        // can keep streaming in without holding the spinner open.
        finalizeSearch();

        // Run background tasks with a small concurrency limit so we don't
        // block rendering or cause the viewer to flicker. We intentionally
        // do NOT toggle isSearching here – these are best-effort refinements.
        if (backgroundTasks.length > 0) {
          runTasksWithConcurrency(backgroundTasks, 3);
        }
      }
    };

    const searchTimeout = setTimeout(() => {
      performSearch();
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(searchTimeout);
      searchIdRef.current += 1;
    };
  }, [
    searchQuery,
    document,
    documentType,
    totalPages,
    isChunked,
    metadata,
    clearSearchQuery,
    setGlobalSearchQuery,
    setGlobalSearchResults,
    setGlobalIsSearching,
    loadChunk,
    loadEpubChapter,
    runTasksWithConcurrency,
  ]);

  useEffect(() => {
    if (!pendingScrollRestoreRef.current) return;

    const restoreScroll = () => {
      const el = searchResultsContentRef.current;
      if (!el) return;
      const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
      const clampedScrollTop = Math.max(
        0,
        Math.min(savedScrollTopRef.current, maxScrollTop),
      );
      el.scrollTop = clampedScrollTop;
    };

    const rafId = window.requestAnimationFrame(restoreScroll);
    const timeoutId = window.setTimeout(restoreScroll, 120);
    pendingScrollRestoreRef.current = false;

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [
    currentPage,
    isSearching,
    globalIsSearching,
    searchResults.length,
    globalSearchResults.length,
  ]);

  // Sync with global search query when sidebar opens or global query changes
  useEffect(() => {
    if (globalSearchQuery !== searchQuery) {
      setSearchQuery(globalSearchQuery || "");
    }
  }, [globalSearchQuery, searchQuery]);

  const handleSearchInputChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchQuery(value);
      setGlobalSearchQuery(value);
    },
    [setGlobalSearchQuery],
  );

  const handleResultClick = useCallback(
    (result) => {
      const resultsContainer = searchResultsContentRef.current;
      if (resultsContainer) {
        savedScrollTopRef.current = resultsContainer.scrollTop;
      }
      pendingScrollRestoreRef.current = true;

      if (documentType === "pdf" && result.pageNumber) {
        setCurrentPage(result.pageNumber);
        // Ensure PDF viewer scrolls into view immediately
        window.dispatchEvent(
          new CustomEvent("reader:go-to-page", {
            detail: { page: result.pageNumber },
          }),
        );
      } else if (documentType === "epub" && result.pageNumber) {
        setCurrentPage(result.pageNumber);
      }
      if (isMobile) {
        // Hide sidebar panel but keep search state (query + results) so
        // highlights remain visible after navigating to the result.
        hideSearchSidebar();
      }
    },
    [documentType, setCurrentPage, isMobile, hideSearchSidebar],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setGlobalSearchQuery("");
    setSearchResults([]);
    setSearchError(null);
    clearSearchQuery();
    setGlobalSearchResults([]);
    setGlobalIsSearching(false);
  }, [
    clearSearchQuery,
    setGlobalSearchQuery,
    setGlobalSearchResults,
    setGlobalIsSearching,
  ]);

  const handleCloseSidebar = useCallback(() => {
    // Clear search + highlights when user explicitly closes the sidebar
    handleClearSearch();
    toggleSearchSidebar();
  }, [handleClearSearch, toggleSearchSidebar]);

  // Group results by page/section
  const groupedResults = useMemo(() => {
    const grouped = {};
    searchResults.forEach((result) => {
      // For PDF: use pageNumber directly
      // For EPUB: use synthetic page numbers from metadata when available.
      const pageKey = result.pageNumber || result.sectionIndex + 1;
      if (!grouped[pageKey]) {
        grouped[pageKey] = [];
      }
      grouped[pageKey].push(result);
    });
    return grouped;
  }, [searchResults]);

  const groupedGlobalResults = useMemo(() => {
    const grouped = {};
    globalSearchResults.forEach((result) => {
      const pageKey = result.pageNumber || result.sectionIndex + 1;
      if (!grouped[pageKey]) {
        grouped[pageKey] = [];
      }
      grouped[pageKey].push(result);
    });
    return grouped;
  }, [globalSearchResults]);

  const totalSearchOccurrences = useMemo(
    () => getTotalOccurrences(searchResults),
    [searchResults],
  );

  const totalGlobalSearchOccurrences = useMemo(
    () => getTotalOccurrences(globalSearchResults),
    [globalSearchResults],
  );

  // Mobile topbar writes the same query into global store; desktop uses local
  // state. Keep empty / no-results feedback identical on both.
  const activeResults = isMobile ? globalSearchResults : searchResults;
  const activeSearching = isMobile ? globalIsSearching : isSearching;
  const activeGrouped = isMobile ? groupedGlobalResults : groupedResults;
  const activeTotal = isMobile
    ? totalGlobalSearchOccurrences
    : totalSearchOccurrences;
  const hasQuery = Boolean(searchQuery.trim());

  const renderResults = (results, count) => {
    const resultCount = count ?? searchResults.length;
    return (
      <div className="search-results-container">
        <div role="status" aria-live="polite" className="sr-only">
          {t("search.resultsCount", { count: resultCount })}
        </div>
        <div className="search-results-count">
          {t("search.resultsCount", { count: resultCount })}
        </div>
        {Object.entries(results)
          .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
          .map(([pageNum, matches]) => (
            <div
              key={pageNum}
              className="search-result-item clickable-card"
              onClick={() => handleResultClick(matches[0])}
            >
              <div className="search-result-header">
                <span className="search-result-location">
                  {t("notes.pageTitle", { page: pageNum })}
                </span>
                <span className="search-result-matches">
                  {t("search.matchCount", {
                    count: getTotalOccurrences(matches),
                  })}
                </span>
              </div>
              <div className="search-result-matches-list">
                {matches.map((result, idx) => (
                  <div key={idx} className="search-result-context">
                    {(() => {
                      const rawQuery = result?.query || "";
                      const escapedQuery = escapeRegExp(rawQuery);
                      if (!escapedQuery) {
                        return <span>{result.context}</span>;
                      }

                      const highlightRegex = new RegExp(
                        `(${escapedQuery})`,
                        "gi",
                      );
                      return result.context
                        .split(highlightRegex)
                        .map((part, i) =>
                          part.toLowerCase() === rawQuery.toLowerCase() ? (
                            <mark key={i} className="search-highlight">
                              {part}
                            </mark>
                          ) : (
                            <span key={i}>{part}</span>
                          ),
                        );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div
      className="search-sidebar open"
      role="complementary"
      aria-label={t("search.ariaLabel")}
    >
      {!isMobile && (
        <div className="search-sidebar-header">
          <h3 tabIndex="-1">{t("search.searchResults")}</h3>
          <button
            className="sidebar-close-button"
            onClick={handleCloseSidebar}
            aria-label={t("search.close")}
          >
            <CloseIcon width={24} height={24} />
          </button>
        </div>
      )}

      {/* Only show search input on desktop, not mobile */}
      {!isMobile && (
        <form
          className="search-input-container"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="search"
            className="search-input"
            placeholder={t("search.placeholder")}
            value={searchQuery}
            onChange={handleSearchInputChange}
            autoFocus
            enterKeyHint="search"
          />
          {searchQuery && (
            <button
              className="search-clear-button"
              onClick={handleClearSearch}
              title={t("search.clear")}
              aria-label={t("search.clear")}
              type="button"
            >
              <CrossIcon width={16} height={16} />
            </button>
          )}
        </form>
      )}

      <div className="search-results-content" ref={searchResultsContentRef}>
        {activeSearching && activeResults.length === 0 ? (
          <div className="search-loading" role="status" aria-live="polite">
            <div className="search-spinner" aria-hidden="true"></div>
            <p>{t("search.searching")}</p>
          </div>
        ) : searchError ? (
          <div className="search-error" role="alert" aria-live="assertive">
            <p>{searchError}</p>
          </div>
        ) : hasQuery && activeResults.length === 0 ? (
          <div className="search-empty">
            <p>{t("search.noResults", { query: searchQuery })}</p>
          </div>
        ) : activeResults.length > 0 ? (
          renderResults(activeGrouped, activeTotal)
        ) : (
          <div className="search-empty">
            <p>{t("search.enterQuery")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSidebar;
